/** Ролевая модель (ТЗ п.2): 4 операционные роли + 2 административные. */
export const ROLES = [
  'DISPATCHER',
  'MECHANIC',
  'MEDIC',
  'DRIVER',
  'DRIVER_CHIEF',
  'ADMIN',
] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  DISPATCHER: 'Уполномоченное лицо / Диспетчер',
  MECHANIC: 'Механик КТП',
  MEDIC: 'Медработник',
  DRIVER: 'Водитель',
  DRIVER_CHIEF: 'Начальник водителей',
  ADMIN: 'Администратор',
};

/**
 * Разрешения по ролям — единый источник правды для guard'ов на бэке
 * и для скрытия элементов интерфейса на фронте.
 */
export const PERMISSIONS = [
  'release:read',
  'release:write',
  'release:approve',
  'assignment:read',
  'assignment:issue',
  'assignment:respond',
  'shift:read',
  /**
   * Видеть смены и путевые листы всего парка. У водителя этого права нет:
   * он читает только свои смены, иначе получает доступ к ИИН коллег
   * и примечаниям чужих медосмотров.
   */
  'shift:read-all',
  'shift:write',
  'shift:replace-resource',
  'shift:close',
  'waybill:read',
  'waybill:sign',
  'medical:write',
  'technical:write',
  'directory:read',
  'directory:write',
  'user:manage',
  'audit:read',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  // Создание/корректировка выпуска, контроль готовности, подпись ЭПЛ ЭЦП ЮЛ,
  // фиксация выезда/прибытия, замена ресурсов, закрытие смены.
  DISPATCHER: [
    'release:read',
    'release:write',
    'release:approve',
    'assignment:read',
    'assignment:issue',
    'shift:read',
    'shift:read-all',
    'shift:write',
    'shift:replace-resource',
    'shift:close',
    'waybill:read',
    'waybill:sign',
    'directory:read',
    'audit:read',
  ],
  // Предрейсовый/послерейсовый техосмотр, одометр и топливо, QR-подпись.
  MECHANIC: ['shift:read', 'shift:read-all', 'waybill:read', 'technical:write', 'directory:read'],
  // Регистрация результатов медосмотра, QR-подпись.
  MEDIC: ['shift:read', 'shift:read-all', 'waybill:read', 'medical:write', 'directory:read'],
  // Получение назначения, подтверждение/отказ, 2-кратное подтверждение ПЛ.
  DRIVER: ['assignment:read', 'assignment:respond', 'shift:read', 'waybill:read'],
  // Проект выпуска, распределение водителей/автобусов, устранение конфликтов.
  DRIVER_CHIEF: [
    'release:read',
    'release:write',
    'assignment:read',
    'shift:read',
    'shift:read-all',
    'shift:write',
    'directory:read',
  ],
  // Пользователи, роли, привязка номеров WhatsApp, справочники.
  ADMIN: [
    'user:manage',
    'directory:read',
    'directory:write',
    'audit:read',
    'release:read',
    'shift:read',
    'shift:read-all',
    'waybill:read',
    'assignment:read',
  ],
};

export function can(roles: readonly Role[] | undefined, permission: Permission): boolean {
  if (!roles || roles.length === 0) {
    return false;
  }
  return roles.some((role) => ROLE_PERMISSIONS[role]?.includes(permission));
}
